import type { ZudokuConfig } from 'zudoku';
import { ruRU } from '@clerk/localizations';

const config: ZudokuConfig = {
  page: {
    pageTitle: 'Open API documentation',
    logo: {
      src: {
        light: '/logo_aidu--light.svg',
        dark: '/logo_aidu--dark.svg',
      },
      alt: 'AIDU',
      width: 100,
    },
    banner: {
      message: (
        <div className='text-center'>
          <p>
            По всем вопросам обращайтесь в{' '}
            <a href='https://t.me/elvenioils' className='light:text-blue-500 dark:text-white'>
              телеграм
            </a>
            . For any questions, please contact us on{' '}
            <a href='https://t.me/elvenioils' className='light:text-blue-500 dark:text-white'>
              Telegram
            </a>
            .
          </p>
        </div>
      ),
      color: 'info', // "note" | "tip" | "info" | "caution" | "danger" or custom
      dismissible: true,
    },
  },
  search: {
    type: 'pagefind',
    maxSubResults: 3,
    ranking: {
      termFrequency: 0.8,
      pageLength: 0.6,
      termSimilarity: 1.2,
      termSaturation: 1.2,
    },
  },
  theme: {
    light: {
      background: 'oklch(100% 0 none)',
      foreground: 'oklch(13.7% 0.036 259)',
      card: 'oklch(100% 0 none)',
      cardForeground: 'oklch(13.7% 0.036 259)',
      popover: 'oklch(100% 0 none)',
      popoverForeground: 'oklch(13.7% 0.036 259)',
      primary: 'oklch(54.6% 0.215 263)',
      primaryForeground: 'oklch(98.4% 0.003 248)',
      secondary: 'oklch(96.8% 0.007 248)',
      secondaryForeground: 'oklch(20.8% 0.04 266)',
      muted: 'oklch(96.8% 0.007 248)',
      mutedForeground: 'oklch(55.5% 0.041 257)',
      accent: 'oklch(96.8% 0.007 248)',
      accentForeground: 'oklch(20.8% 0.04 266)',
      destructive: 'oklch(63.7% 0.208 25.3)',
      destructiveForeground: 'oklch(98.4% 0.003 248)',
      border: 'oklch(92.9% 0.013 256)',
      input: 'oklch(92.9% 0.013 256)',
      ring: 'oklch(54.6% 0.215 263)',
    },
    dark: {
      background: 'oklch(13.7% 0.036 259)',
      foreground: 'oklch(98.4% 0.003 248)',
      card: 'oklch(13.7% 0.036 259)',
      cardForeground: 'oklch(98.4% 0.003 248)',
      popover: 'oklch(13.7% 0.036 259)',
      popoverForeground: 'oklch(98.4% 0.003 248)',
      primary: 'oklch(62.3% 0.188 260)',
      primaryForeground: 'oklch(20.8% 0.04 266)',
      secondary: 'oklch(28% 0.037 260)',
      secondaryForeground: 'oklch(98.4% 0.003 248)',
      muted: 'oklch(28% 0.037 260)',
      mutedForeground: 'oklch(71.1% 0.035 257)',
      accent: 'oklch(28% 0.037 260)',
      accentForeground: 'oklch(98.4% 0.003 248)',
      destructive: 'oklch(39.6% 0.133 25.7)',
      destructiveForeground: 'oklch(98.4% 0.003 248)',
      border: 'oklch(28% 0.037 260)',
      input: 'oklch(28% 0.037 260)',
      ring: 'oklch(48.8% 0.217 264)',
    },
    // @ts-ignore
    customCss: `
      ".lucide": {
        flex-shrink: 0 !important;
      }
    `,
  },
  topNavigation: [
    { id: 'docs', label: 'Документация' },
    { id: 'api', label: 'API' },
    { id: 'docs-eng', label: 'Documentation -- eng' },
    { id: 'api-eng', label: 'API -- eng' },
  ],
  sidebar: {
    docs: [
      {
        type: 'doc',
        id: 'docs/introduction',
        label: 'Вступление',
        icon: 'book-open',
      },
      {
        type: 'doc',
        id: 'docs/onboarding',
        label: 'Онбординг',
        icon: 'book-open-check',
      },
      {
        type: 'doc',
        id: 'docs/stores',
        label: 'Магазины и отделы',
        icon: 'store',
      },
      {
        type: 'doc',
        id: 'docs/categories',
        label: 'Категории и подкатегории',
        icon: 'book-copy',
      },
      {
        type: 'category',
        label: 'Исполнители и Подряд...',
        icon: 'user',
        items: [
          {
            type: 'doc',
            id: 'docs/executors',
            label: 'Исполнители',
            icon: 'user-cog',
          },
          {
            type: 'doc',
            id: 'docs/contractors',
            label: 'Подрядчики',
            icon: 'file-user',
          },
        ],
      },
      {
        type: 'category',
        label: 'Услуги',
        icon: 'rows-4',
        items: [
          {
            type: 'doc',
            id: 'docs/services',
            label: 'Услуги',
            icon: 'list-tree',
          },
          {
            type: 'doc',
            id: 'docs/custom-fields-service',
            label: 'Кастомные поля услуг',
            icon: 'settings',
          },
        ],
      },
      {
        type: 'category',
        label: 'Заказы',
        icon: 'shopping-cart',
        items: [
          {
            type: 'doc',
            id: 'docs/orders',
            label: 'Заказы',
            icon: 'shopping-cart',
          },
          {
            type: 'doc',
            id: 'docs/custom-fields-order',
            label: 'Кастомные поля заказа',
            icon: 'settings',
          },
          {
            type: 'doc',
            id: 'docs/orders-admin',
            label: 'Адмнистрирование заказов',
            icon: 'scan-barcode',
          },
          {
            type: 'doc',
            id: 'docs/feedback',
            label: 'Отзывы и орбатная связь',
            icon: 'message-circle',
          },
          {
            type: 'doc',
            id: 'docs/schedule',
            label: 'Расписание и доступность',
            icon: 'calendar-days',
          },
          {
            type: 'doc',
            id: 'docs/category-schedule',
            label: 'Расписание по категориям',
            icon: 'calendar-x',
          },
        ],
      },
      {
        type: 'category',
        label: 'Справочные данные',
        icon: 'badge-info',
        items: [
          {
            type: 'doc',
            id: 'docs/reference-data',
            label: 'Справочные данные',
            icon: 'badge-info',
          },
          {
            type: 'doc',
            id: 'docs/client-profile',
            label: 'Профиль клиента',
            icon: 'user-circle',
          },
        ],
      },
      {
        type: 'category',
        label: 'Примеры ',
        icon: 'code',
        items: [
          {
            type: 'doc',
            id: 'docs/examples',
            label: 'Создание услуги',
            icon: 'square-chevron-right',
          },
        ],
      },
      {
        type: 'doc',
        id: 'docs/finances',
        label: 'Финансы',
        icon: 'credit-card',
      },
    ],
    docs_eng: [
      {
        type: 'doc',
        id: 'docs-eng/introduction',
        label: 'Introduction',
        icon: 'book-open',
      },
      {
        type: 'doc',
        id: 'docs-eng/onboarding',
        label: 'Onboarding',
        icon: 'book-open-check',
      },
      {
        type: 'doc',
        id: 'docs-eng/stores',
        label: 'Stores and Departments',
        icon: 'store',
      },
      {
        type: 'doc',
        id: 'docs-eng/categories',
        label: 'Categories and Subcategories',
        icon: 'book-copy',
      },
      {
        type: 'category',
        label: 'Executors and Contractors',
        icon: 'user',
        items: [
          {
            type: 'doc',
            id: 'docs-eng/executors',
            label: 'Executors',
            icon: 'user-cog',
          },
          {
            type: 'doc',
            id: 'docs-eng/contractors',
            label: 'Contractors',
            icon: 'file-user',
          },
        ],
      },
      {
        type: 'category',
        label: 'Services',
        icon: 'rows-4',
        items: [
          {
            type: 'doc',
            id: 'docs-eng/services',
            label: 'Services',
            icon: 'list-tree',
          },
          {
            type: 'doc',
            id: 'docs-eng/custom-fields-service',
            label: 'Service custom fields',
            icon: 'settings',
          },
        ],
      },
      {
        type: 'category',
        label: 'Orders',
        icon: 'shopping-cart',
        items: [
          {
            type: 'doc',
            id: 'docs-eng/orders',
            label: 'Orders',
            icon: 'shopping-cart',
          },
          {
            type: 'doc',
            id: 'docs-eng/custom-fields-order',
            label: 'Order custom fields',
            icon: 'settings',
          },
          {
            type: 'doc',
            id: 'docs-eng/orders-admin',
            label: 'Order administration',
            icon: 'scan-barcode',
          },
          {
            type: 'doc',
            id: 'docs-eng/feedback',
            label: 'Reviews and feedback',
            icon: 'message-circle',
          },
          {
            type: 'doc',
            id: 'docs-eng/schedule',
            label: 'Schedule and availability',
            icon: 'calendar-days',
          },
          {
            type: 'doc',
            id: 'docs-eng/category-schedule',
            label: 'Category schedule',
            icon: 'calendar-x',
          },
        ],
      },
      {
        type: 'category',
        label: 'Reference data',
        icon: 'badge-info',
        items: [
          {
            type: 'doc',
            id: 'docs-eng/reference-data',
            label: 'Reference data',
            icon: 'badge-info',
          },
          {
            type: 'doc',
            id: 'docs-eng/client-profile',
            label: 'Client profile',
            icon: 'user-circle',
          },
        ],
      },
      {
        type: 'category',
        label: 'Examples',
        icon: 'code',
        items: [
          {
            type: 'doc',
            id: 'docs-eng/examples',
            label: 'Create a service',
            icon: 'square-chevron-right',
          },
        ],
      },
      {
        type: 'doc',
        id: 'docs-eng/finances',
        label: 'Finances',
        icon: 'credit-card',
      },
    ],
  },

  redirects: [
    { from: '/', to: '/docs/introduction' },
    { from: '/docs-eng', to: '/docs-eng/introduction' },
  ],
  apis: [
    {
      type: 'url',
      input: 'https://api.terminal.aidu.me/api/v1/openapi.yaml',
      navigationId: 'api',
    },
    {
      type: 'url',
      input: 'https://api.terminal.aidu.me/api/v1/en/openapi.yaml',
      navigationId: 'api-eng',
    },
  ],
  docs: {
    files: '/pages/**/*.{md,mdx}',
  },
  authentication: {
    type: 'clerk',
    // @ts-ignore
    clerkPubKey: process.env.CLERK_PUBLIC_KEY || 'pk_test_ZnVsbC1kcnVtLTUzLmNsZXJrLmFjY291bnRzLmRldiQ',
  },
  protectedRoutes: [
    '/docs/*', // Protect the settings page
    '/api/*', // Protect all API-related routes
    '/docs-eng/*',
    '/api-eng/*',
  ],
  // deploy3
};

export default config;
